import React from 'react';
import { Text, View, Image, Pressable } from 'react-native';
import { Button } from 'LocalGluons';
import { Device } from 'DeviceUtils';
import { useStore } from 'Store/Store';
import { t } from 'Locale';
import type { ConsentDefinition, NativeConsentDefinition } from 'types';
import { ConsentsToSaveRequest } from '@24i/st-api-connector';
import Theme from 'Theme';
import { linkCommon } from 'Theme/DialogCommon.jss';
import renderHTMLText from '../../RenderHtmlText/renderHtmlText';
import style from './CanalplusConsentPopup.jss';
import CanalplusConsentDialog from '../CanalplusConsentDialog';

interface ConsentsResponse {
    consents: ConsentDefinition[];
    detail: {
        infoDescription: string,
        infoImageUrlLandscape: string,
        infoImageUrlPortrait: string,
        title: string,
        titleDescription: string,
    };
    popup: {
        badgeImageUrlPortrait: string;
        badgeImageUrlLandscape: string;
        logoImageUrlPortrait: string;
        logoImageUrlLandscape: string;
        description: string,
        title: string
    },
    item: any
}

interface CanalplusPopupProps {
    navigation: any;
    onAccept: () => void;
    setModalContent: (state: any) => void;
    consentsResponse: ConsentsResponse;
    setShowConsentsAgain: React.Dispatch<React.SetStateAction<boolean>>;
}

const CanalplusPopup = ({
    navigation,
    consentsResponse,
    onAccept,
    setModalContent,
    setShowConsentsAgain,
}: CanalplusPopupProps) => {
    const { consents, detail, item, popup } = consentsResponse;
    const { appManager } = useStore();
    const {
        backstageConnector: { auth }
    } = appManager.getApi();

    const onClose = () => setModalContent(null);
    const handleCustomizeConsents = (): void => {
        if (Device.isWeb) {
            setModalContent(
                <CanalplusConsentDialog
                    isLandscape={Device.isTabletOrWeb}
                    titleDescription={detail.titleDescription}
                    title={detail.title}
                    consentDefinitions={consents}
                    setShowConsentsAgain={setShowConsentsAgain}
                    onAccept={onAccept}
                    onClose={onClose}
                />
            );
        } else {
            navigation.navigate('CanalplusConsent', {
                isLandscape: Device.isTabletOrWeb,
                titleDescription: detail.titleDescription,
                title: detail.title,
                consentDefinitions: consents,
                setShowConsentsAgain,
                onAccept,
            });
            // there is delay between switching screens
            setTimeout(onClose, 10);
        }
        setShowConsentsAgain(false);
    };

    const handleAcceptAll = async () => {
        try {
            const consentsToSaveRequest: ConsentsToSaveRequest = {
                consents: consents.map((consent: ConsentDefinition) => ({
                    name: consent.name,
                    value: true
                }))
            };
            await auth.saveConsents('CANALPLUS', consentsToSaveRequest);
            onAccept();
            // const loggedUserData = await appManager.getLoggedUserObjectDataFromStorage();
            // await appManager.refreshToken(loggedUserData);
            onClose();
            setShowConsentsAgain(false);
        } catch (error) {
            console.log(error);
        }
    };

    const description = {
        html: `
            <div style="text-align: center;">
                ${t('PARTNER.CANALPLUS.CONSENTS.CONSENT1_DESCRIPTION_MOBILES')}
            </div>
        `
    };
    return (
        <>
            {Device.isPhone && (
                <View style={style.container}>
                    <View style={style.overlayMobile}>
                        <View style={style.contentContainer}>
                            <Image
                                source={{ uri: popup.logoImageUrlPortrait }}
                                style={style.logo}
                            />
                            <Image
                                source={{ uri: popup.badgeImageUrlPortrait }}
                                style={style.badgePhone}
                            />
                            <Text style={style.title}>{t('PARTNER.CANALPLUS.CONSENTS.TITLE_MOBILES')}</Text>
                            <View style={style.divider} />
                            <View style={style.description}>
                                {renderHTMLText(description, {}, { a: linkCommon })}
                            </View>
                        </View>
                        <Pressable
                            onPress={handleCustomizeConsents}
                            style={style.buttonDetailed}
                        >
                            <Text style={style.buttonText}>
                                {t('PARTNER.CANALPLUS.CONSENTS.SETTINGS_BTN_MOBILES')}
                            </Text>
                        </Pressable>

                        <Button
                            title={t('PARTNER.CANALPLUS.CONSENTS.I_AGREE')}
                            onPress={handleAcceptAll}
                        />
                    </View>
                </View >
            )}
            {
                Device.isTabletOrWeb && (
                    <View style={style.container}>
                        <View style={Device.isTablet ? style.overlayTablet : style.overlayWeb}>
                            <Image
                                source={{ uri: popup.badgeImageUrlLandscape }}
                                style={style.badge}
                            />
                            {Device.isTablet && (
                                <Image
                                    source={{ uri: popup.logoImageUrlLandscape }}
                                    style={style.logo}
                                />
                            )}
                            {Device.isWeb && (
                                <Image
                                    source={{ uri: popup.logoImageUrlLandscape }}
                                    style={style.webLogo}
                                />
                            )}
                            <Text style={style.title}>{t('PARTNER.CANALPLUS.CONSENTS.TITLE_MOBILES')}</Text>
                            {/* <View style={style.divider} /> */}
                            <View style={style.tabletDescription}>
                                {renderHTMLText(description, {}, { a: linkCommon })}
                            </View>
                            <View style={style.alignmentsOnWebOrTablet}>
                                <Button
                                    title={t('PARTNER.CANALPLUS.CONSENTS.SETTINGS_BTN_MOBILES')}
                                    onPress={handleCustomizeConsents}
                                    style={{
                                        button: {
                                            ...style.button,
                                            ...style.cancelButton
                                        },
                                        textContainer: style.buttonContainerText,
                                        text: style.buttonText
                                    }}
                                />
                                <Button
                                    title={t('PARTNER.CANALPLUS.CONSENTS.I_AGREE')}
                                    onPress={handleAcceptAll}
                                    style={{
                                        button: style.button,
                                        textContainer: style.buttonContainerText,
                                        text: style.buttonText
                                    }}
                                />
                            </View>
                        </View>
                    </View >
                )
            }
        </>
    );
};

export default CanalplusPopup;
